import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
    Autocomplete,
    Backdrop,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    Dialog,
    FormControlLabel,
    FormHelperText,
    Stack,
    TextField
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers-pro';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { endOfDay } from 'date-fns';
import { useSnackbar } from 'notistack';
import { FieldError } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { useDebounce } from 'usehooks-ts';
import { ZodIssueCode } from 'zod';
import { LocationUsersV3 } from '../../../backbone-unorganized/shared/forms';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { useBackbone } from '../providers/BackboneProvider';
import { toDate } from './FollowUpActionForm';
import {
    FollowUpActionType,
    IncompleteFollowUpActionType,
    IncompleteFollowUpActionSchema,
    ReplyType,
    FollowUpActionImageType,
    FollowUpActionFileType,
    TaskItemContext,
    AllReplyTypes
} from './FollowUpActions.types';
import { FollowUpActionsGallery } from './FollowUpActionsGallery';
import { FUAReplies } from './ShowFollowUpAction';
import { LegacyApiModel } from './types';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        autoSelect: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                minHeight: '36px',
                border: 'none',
                background: 'none',
                padding: `${spacing(2)} 0`,
                color: 'black !important'
            },
            '& .MuiFormControl-root': {
                height: 'auto'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: spacing(2),
            textTransform: 'none',
            backgroundColor: 'white'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        formContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(10),
            padding: spacing(2)
        },
        formInput: {
            marginTop: spacing(2),
            '& .MuiInputBase-input': {
                border: 'none',
                padding: `${spacing(2)} 0`,
                borderBottom: 'groove'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        textFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        },
        dateFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                padding: `${spacing(2.5)} 0`,
                border: 'none',
                color: 'black !important'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: 'groove'
            },
            '& .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        }
    };
});

interface EditSavedFollowUpActionProps {
    followUpAction: FollowUpActionType;
    onGoBack: () => void;
    open: boolean;
    onSuccessfulUpdate: (followUpAction: FollowUpActionType) => void;
}

export const EditSavedFollowUpAction = (props: EditSavedFollowUpActionProps) => {
    const { followUpAction, onGoBack, open, onSuccessfulUpdate } = props;
    const { classes } = useStyles();
    const { getS3URL, uploadFilesToS3 } = useBackbone<TaskItemContext>();
    const { enqueueSnackbar } = useSnackbar();
    const convertReplyType = (replyType: string): ReplyType => {
        if (replyType === FUAReplies.BASIC || replyType === FUAReplies.PHOTO) {
            return AllReplyTypes[0];
        }
        if (replyType === FUAReplies.FORM) {
            return AllReplyTypes[1];
        }
        throw Error('Unknown reply type');
    };
    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useMuiForm<IncompleteFollowUpActionType>({
        resolver: zodResolver(IncompleteFollowUpActionSchema),
        defaultValues: {
            assigned_user: followUpAction.assigned_user,
            description: followUpAction.data.description!,
            due_date: toDate(followUpAction.date_due!)!,
            files: [],
            form: followUpAction.data.form,
            reply_type: convertReplyType(followUpAction.reply_type),
            require_photo: followUpAction.reply_type === FUAReplies.PHOTO,
            title: followUpAction.title
        }
    });

    const getError = (error?: FieldError) => {
        const zodErrorCodeMap: Record<string, string | undefined> = {
            invalid_type: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_small: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_big: _t('mobile_mobileform_add_follow_up_actions_forms_too_many_characters')
        } satisfies Partial<Record<ZodIssueCode, string>>;

        return error
            ? {
                  error: true,
                  helperText: zodErrorCodeMap[error.type] ?? _t('mobile_mobileform_add_follow_up_actions_forms_generic')
              }
            : {};
    };

    const { field: assigneeField } = useMuiController({ control, name: 'assigned_user' });
    const { field: descriptionField } = useMuiController({ control, name: 'description' });
    const { field: dueDateField } = useMuiController({ control, name: 'due_date' });
    const { field: requirePhotoField } = useMuiController({ control, name: 'require_photo' });
    const { field: titleField } = useMuiController({ control, name: 'title' });

    const [savedFiles, setSavedFiles] = useState<FollowUpActionImageType[]>(
        followUpAction.data.images?.map((filePath) => ({ s3_key: filePath }) as FollowUpActionImageType) ?? []
    );
    const [uploadedFiles, setUploadedFiles] = useState<FollowUpActionFileType[]>([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [assignees, setAssignees] = useState<{ id: number; display_name: string }[]>([]);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const debouncedAssigneeSearch = useDebounce(assigneeSearch, 200);

    useEffect(() => {
        const mobileAssignees = new LocationUsersV3([]);
        mobileAssignees.location_id = followUpAction.location.id;
        mobileAssignees.paginator.set('search', debouncedAssigneeSearch);

        mobileAssignees.fetch({
            success: function () {
                const assigneeData = mobileAssignees.models.map((model: LegacyApiModel) => ({
                    id: model.get('id'),
                    display_name: model.get('display_name')
                }));
                setAssignees(assigneeData);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_users')), {
                    variant: 'error'
                });
            }
        });
    }, [debouncedAssigneeSearch]);

    const onSubmit = handleSubmit(async (values) => {
        const images: string[] = [
            ...(savedFiles.filter((file) => Boolean(file.s3_key)).map((file) => file.s3_key) as string[]),
            ...(uploadedFiles
                .filter((file) => Boolean(file.s3_key))
                .map((uploadedFile) => uploadedFile.s3_key) as string[])
        ];

        followUpAction.assigned_user = values.assigned_user ?? null;
        followUpAction.date_due = values.due_date;
        if (values.require_photo) {
            followUpAction.reply_type = FUAReplies.PHOTO;
        } else {
            followUpAction.reply_type = FUAReplies.BASIC;
        }
        followUpAction.title = values.title;
        followUpAction.data = {
            description: values.description,
            images,
            reply: {}
        };

        await onSuccessfulUpdate(followUpAction);
    });

    const handleOnDeleteSavedImage = (indexToRemove: number) => {
        setSavedFiles((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const handleOnUploadImages = (images: FollowUpActionFileType[]) => {
        setUploadedFiles((prevImages) => [...prevImages, ...images]);
    };

    const handleOnDeleteUploadedImage = (indexToRemove: number) => {
        setUploadedFiles((prevImages) => prevImages.filter((_, index) => indexToRemove !== index));
    };

    const getToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onGoBack}
            slotProps={{
                paper: {
                    sx: {
                        overflow: 'hidden'
                    }
                }
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <PageLayout>
                    {{
                        appBar: (
                            <AppBar
                                onGoBack={onGoBack}
                                title={_t('mobile_mobileform_add_follow_up_actions_forms_update_button')}
                            />
                        ),
                        content: (
                            <Container component="div" className={classes.formContainer}>
                                {showSpinner && (
                                    <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}>
                                        <CircularProgress />
                                    </Backdrop>
                                )}

                                <Stack component="form" onSubmit={onSubmit}>
                                    <FollowUpActionsGallery
                                        getS3URL={getS3URL}
                                        isReply={false}
                                        onDeleteSavedImage={handleOnDeleteSavedImage}
                                        onDeleteUploadedImage={handleOnDeleteUploadedImage}
                                        onUploadFiles={handleOnUploadImages}
                                        photoQualityAnalysis={false}
                                        savedFiles={savedFiles}
                                        uploadedFiles={uploadedFiles}
                                        uploadFilesToS3={uploadFilesToS3}
                                        onLoadingChange={setShowSpinner}
                                    />

                                    <TextField
                                        className={classes.formInput}
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_title')}
                                        required
                                        variant="filled"
                                        {...titleField}
                                        {...getError(errors.title)}
                                    />

                                    <TextField
                                        className={classes.textFieldInput}
                                        data-testid="controlled-input2"
                                        error={getError(errors.description).error}
                                        multiline
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_description')}
                                        required
                                        variant="filled"
                                        {...descriptionField}
                                    />
                                    {errors.description && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.description).helperText}
                                        </FormHelperText>
                                    )}

                                    <FormControlLabel
                                        sx={{ padding: 1 }}
                                        control={
                                            <Checkbox
                                                checked={requirePhotoField.value}
                                                sx={{ marginLeft: 1 }}
                                                {...requirePhotoField}
                                            />
                                        }
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_photo')}
                                    />
                                    <MobileDatePicker
                                        className={classes.dateFieldInput}
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_due_date')}
                                        minDate={getToday()}
                                        slotProps={{
                                            textField: {
                                                error: getError(errors.due_date).error,
                                                required: true,
                                                slotProps: { input: { endAdornment: <CalendarTodayIcon /> } },
                                                variant: 'filled'
                                            }
                                        }}
                                        value={dueDateField.value ?? null}
                                        onChange={(newDate) => {
                                            dueDateField.onChange(newDate ? endOfDay(newDate) : null);
                                        }}
                                    />
                                    {errors.due_date && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.due_date).helperText}
                                        </FormHelperText>
                                    )}

                                    <Autocomplete
                                        className={classes.autoSelect}
                                        disablePortal
                                        getOptionKey={(option) => option.id}
                                        getOptionLabel={(option) => option.display_name}
                                        onChange={(_event, newValue) => {
                                            assigneeField.onChange(newValue);
                                        }}
                                        options={assignees}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={_t('mobile_common_assigned_to')}
                                                onBlur={() => setAssigneeSearch('')}
                                                onChange={(e) => setAssigneeSearch(e.target.value)}
                                                sx={{ height: 60 }}
                                                value={assigneeSearch}
                                                variant="filled"
                                                error={getError(errors.assigned_user as FieldError).error}
                                            />
                                        )}
                                        value={assigneeField.value ?? null}
                                    />

                                    {'assigned_user' in errors && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.assigned_user as FieldError).helperText}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </Container>
                        ),
                        bottomBar: (
                            <BottomBar>
                                <div className={classes.bottomBar}>
                                    <Button
                                        className={classes.bottomButton}
                                        color="primary"
                                        data-testid="addFollowUpButton"
                                        disabled={showSpinner}
                                        onClick={onSubmit}
                                        variant="contained"
                                    >
                                        {_t('mobile_mobileform_add_follow_up_actions_forms_update_button')}
                                    </Button>
                                </div>
                            </BottomBar>
                        )
                    }}
                </PageLayout>
            </LocalizationProvider>
        </Dialog>
    );
};
